// index.tsx
import React, { useEffect, useState } from "react";
import { Table } from "antd";
import type { TableProps } from "antd";
import { DatatableProps } from "../../data/interface"; // Ensure correct path
import { SearchOutlined } from "@ant-design/icons";

const Datatable: React.FC<
  DatatableProps & {
    onSearchChange?: (text: string) => void;
    pagination?: {
      current: number;
      pageSize: number;
      total: number;
      onChange: (page: number, pageSize?: number) => void;
      pageSizeOptions?: number[];
    };
  }
> = ({
  columns,
  dataSource,
  Selection,
  onSelectionChange,
  rowKey = "id",
  selectedKeys = [],
  getCheckboxProps,
  onSearchChange,
  pagination,
}) => {
  const [searchText, setSearchText] = useState<string>("");
  const [Selections, setSelections] = useState<any>(Selection);
  const [filteredDataSource, setFilteredDataSource] = useState(dataSource);

  const handleSelectChange = (newKeys: React.Key[]) => {
    onSelectionChange?.(newKeys); // Propagate changes to parent
  };

  const handleSearch = () => {
    const filteredData = dataSource.filter((record) =>
      Object.values(record).some((field) =>
        String(field).toLowerCase().includes(searchText.toLowerCase())
      )
    );
    setFilteredDataSource(filteredData);
    onSearchChange?.(searchText); // Pass searchText to parent only when search button is clicked
  };

  const rowSelection = Selection
    ? {
        selectedRowKeys: selectedKeys,
        onChange: handleSelectChange,
        preserveSelectedRowKeys: true,
        getCheckboxProps: (record: any) => ({
          id: record[rowKey as keyof typeof record],
          ...(getCheckboxProps ? getCheckboxProps(record) : {}),
        }),
      }
    : undefined;

  // Update Selections when Selection prop changes
  useEffect(() => {
    setSelections(Selection);
  }, [Selection]);

  // Update filteredDataSource when dataSource changes
  useEffect(() => {
    setFilteredDataSource(dataSource);
  }, [dataSource]);

  const tableProps: TableProps<any> = {
    className: "table datanew dataTable no-footer",
    columns,
    dataSource: filteredDataSource,
    rowKey: rowKey,
    pagination: {
      current: pagination?.current,
      pageSize: pagination?.pageSize,
      total: pagination?.total,
      onChange: pagination?.onChange,
      pageSizeOptions: pagination?.pageSizeOptions || [10, 20, 30],
      showSizeChanger: true,
      showLessItems: true,
      locale: {
        items_per_page: "",
      },
    },
  };

  return (
    <>
      <div className="table-top-data d-flex px-3 justify-content-between">
        <div className="page-range"></div>
        <div className="search-global text-right">
          <div className="input-group mb-3" style={{ width: 320 }}>
            <input
              type="search"
              className="form-control form-control-sm"
              value={searchText}
              placeholder="Search"
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              style={{ height: 32 }}
              aria-controls="DataTables_Table_0"
            />
            <button
              type="button"
              className="btn btn-sm btn-primary d-flex align-items-center justify-content-center"
              onClick={handleSearch}
              style={{
                cursor: "pointer",
                width: 40,
                height: 32,
                padding: 0,
              }}
            >
              <SearchOutlined style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
      </div>
      <Table {...tableProps} {...(Selections && { rowSelection })} />
    </>
  );
};

export default Datatable;
